import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, LogIn } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useApiBase } from "../context/ApiBaseContext";
import { useBeta } from "../context/BetaContext";

export default function JoinProject() {
    const navigate = useNavigate();
    const apiBase = useApiBase();
    const { refresh: refreshBeta } = useBeta();
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const normalized = code.trim().toUpperCase();
        try {
            // Iter-158.12.9 — try the legacy endpoint first (which
            // matches this page's ApiBaseProvider value="/projects").
            // If that 404s, transparently try the beta endpoint so
            // producers who copy the invite code from a
            // `/join/beta/{code}` URL and paste it here manually
            // don't hit a dead-end "Invalid invite code" message.
            let data;
            try {
                const res = await api.post(`${apiBase}/join`, {
                    invite_code: normalized,
                });
                data = res.data;
                navigate(`/projects/${data.id}`, { replace: true });
                return;
            } catch (err) {
                if (err?.response?.status !== 404) throw err;
                // Fallback: beta invite code.
                const res = await api.post("/beta/projects/join", {
                    invite_code: normalized,
                });
                data = res.data;
                // Iter-158.12.9 — refresh BetaContext BEFORE nav so
                // BetaGate sees the freshly-granted multi_dept flag.
                try {
                    await refreshBeta();
                } catch {
                    /* non-fatal */
                }
                // Iter-158.13.2.7 — Manual entry now honours the
                // server-authoritative landing_url in the SAME way
                // the deep-link path (JoinBetaProject.jsx) does. A
                // T3 dept-scoped code entered manually lands the
                // user in the department workspace, not the
                // Production Hub. A T1 hub-scoped code still lands
                // at the Hub because that's the landing_url the
                // backend returns for hub-scope resolution.
                if (typeof data.landing_url === "string" && data.landing_url) {
                    navigate(data.landing_url, { replace: true });
                } else {
                    navigate(`/beta/projects/${data.id}`, { replace: true });
                }
                return;
            }
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="join-project-page" className="min-h-screen">
            <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-white/10">
                <div className="max-w-2xl mx-auto px-5 h-16 flex items-center gap-3">
                    <Link
                        data-testid="back-to-projects"
                        to="/"
                        className="tap inline-flex items-center gap-2 rounded-md px-3 h-10 text-sm font-medium text-neutral-200 hover:bg-white/5"
                    >
                        <ArrowLeft size={16} />
                        Back
                    </Link>
                    <div className="font-display font-bold text-white">Join a project</div>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-5 pt-6 pb-32">
                <div className="mb-6">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                        Invite code
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl font-black text-white mt-2">
                        Enter your 6-character code
                    </h1>
                    <p className="text-neutral-400 text-sm mt-2">
                        Got a share link? The code is the last segment in the URL
                        (e.g. <span className="mono text-neutral-300">/join/AB3C7K</span>).
                    </p>
                </div>

                <form onSubmit={submit} className="space-y-5">
                    <div className="space-y-2">
                        <Label htmlFor="invite-code" className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                            Invite code
                        </Label>
                        <Input
                            id="invite-code"
                            data-testid="invite-code-input"
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            placeholder="AB3C7K"
                            required
                            autoCapitalize="characters"
                            autoCorrect="off"
                            spellCheck={false}
                            maxLength={12}
                            className="mono h-14 text-2xl tracking-[0.4em] text-center bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                        />
                    </div>

                    {error ? (
                        <p data-testid="join-error" className="text-sm text-red-400">
                            {error}
                        </p>
                    ) : null}

                    <Button
                        data-testid="join-submit"
                        type="submit"
                        disabled={loading || code.trim().length < 6}
                        className="w-full min-tap font-semibold tracking-wide"
                    >
                        <LogIn size={16} className="mr-2" />
                        {loading ? "Joining…" : "Join project"}
                    </Button>
                </form>
            </main>
        </div>
    );
}
